import React, { useEffect, useRef, useState } from "react";
import { Button, Tooltip } from "antd";
import { DownloadOutlined, FileOutlined, LoadingOutlined, ReadOutlined } from "@ant-design/icons";
import { TagAssociatedDocument } from "@/lib/types";
import DialogPopupHeader from "@/components/DialogPopup/DialogPopupHeader/DialogPopupHeader";
import FileTypeIcon, { hasMappedFileTypeIcon } from "@/components/ui/FileTypeIcon";
import { useEnvironment } from "@/context/EnvironmentContext";

import "../../DialogPopup/DialogPopup.css";
import "../../DialogPopup/DialogPopupFooter/DialogPopupFooter.css";
import "../../DialogPopup/DialogPopupSourceDocuments/DialogPopupSourceDocuments.css";

interface SectionTagDocumentsModalProps {
  downloadDocument: (documentId: string) => Promise<void>;
  documents: TagAssociatedDocument[];
  error: string | null;
  isLoading: boolean;
  onClose: () => void;
  open: boolean;
  sectionDisplayName: string;
  tagColor: string;
  tagDescription?: string;
  tagName: string;
}

function SectionTagDocumentsModal({
  downloadDocument,
  documents,
  error,
  isLoading,
  onClose,
  open,
  sectionDisplayName,
  tagColor,
  tagDescription,
  tagName,
}: SectionTagDocumentsModalProps) {
  const { selectedEnvironment } = useEnvironment();
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [open, onClose]);

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [open, tagDescription, tagName]);

  useEffect(() => {
    if (!open || !tagDescription || isDescriptionExpanded) {
      return undefined;
    }

    const measureOverflow = () => {
      const descriptionElement = descriptionRef.current;

      if (!descriptionElement) {
        return;
      }

      setIsDescriptionOverflowing(descriptionElement.scrollHeight > descriptionElement.clientHeight + 1);
    };

    measureOverflow();

    if (typeof ResizeObserver !== "undefined" && descriptionRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        measureOverflow();
      });

      resizeObserver.observe(descriptionRef.current);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener("resize", measureOverflow);

    return () => {
      window.removeEventListener("resize", measureOverflow);
    };
  }, [open, tagDescription, isDescriptionExpanded]);

  if (!open) {
    return null;
  }

  const modalSubtitle = `${sectionDisplayName} - ${tagName}`;
  const workspaceUrl = selectedEnvironment?.workspace_url;
  const projectsUrl = workspaceUrl ? `${workspaceUrl}/projects` : null;
  const isProjectsLinkDisabled = !projectsUrl;

  return (
    <div className="dialog-popup-overlay" onClick={onClose}>
      <div
        className="dialog-popup-container"
        onClick={(event) => event.stopPropagation()}
        aria-modal="true"
        aria-labelledby="tag-documents-modal-title"
      >
        <DialogPopupHeader
          sectionDialogContent={{ section: { display_name: modalSubtitle } }}
          dialogPopupTitle="Documents"
        >
          <ReadOutlined />
        </DialogPopupHeader>

        <div className="dialog-popup-content">
          <div className="dialog-popup-source-documents-section">
            <div className="dialog-popup-source-documents-section-label">
              <span id="tag-documents-modal-title" className="dialog-popup-source-documents-section-title">
                Files for <span style={{ color: tagColor }}>{tagName}</span>
              </span>
              {tagDescription ? (
                <>
                  <span
                    ref={descriptionRef}
                    className={`dialog-popup-source-documents-section-description dialog-popup-source-documents-section-description-tag ${
                      isDescriptionExpanded ? "expanded" : "clamped"
                    }`}
                  >
                    {tagDescription}
                  </span>
                  {isDescriptionOverflowing && (
                    <button
                      type="button"
                      className="dialog-popup-source-documents-section-description-toggle"
                      onClick={(event) => {
                        event.stopPropagation();
                        setIsDescriptionExpanded((prevState) => !prevState);
                      }}
                    >
                      {isDescriptionExpanded ? "See less" : "See more"}
                    </button>
                  )}
                </>
              ) : null}
              <span className="dialog-popup-source-documents-section-description">
                Associated files in the current project. Browse in{" "}
                <a
                  href={projectsUrl || undefined}
                  target={isProjectsLinkDisabled ? undefined : "_blank"}
                  rel={isProjectsLinkDisabled ? undefined : "noreferrer"}
                  aria-disabled={isProjectsLinkDisabled}
                  className={`dialog-popup-source-documents-section-description-link ${
                    isProjectsLinkDisabled ? "dialog-popup-source-documents-section-description-link-disabled" : ""
                  }`}
                  onClick={(event) => {
                    event.stopPropagation();

                    if (isProjectsLinkDisabled) {
                      event.preventDefault();
                    }
                  }}
                >
                  projects
                </a>
              </span>
            </div>

            <div className="dialog-popup-source-documents-sources">
              {isLoading ? (
                <div className="dialog-popup-source-documents-source-card">
                  <div className="dialog-popup-source-documents-source-header">
                    <div className="dialog-popup-source-documents-source-info">
                      <div className="dialog-popup-source-documents-source-icon">
                        <LoadingOutlined />
                      </div>
                      <div className="dialog-popup-source-documents-source-details">
                        <div className="dialog-popup-source-documents-source-name">Loading associated files...</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : error ? (
                <div className="dialog-popup-source-documents-source-card">
                  <div className="dialog-popup-source-documents-source-header">
                    <div className="dialog-popup-source-documents-source-info">
                      <div className="dialog-popup-source-documents-source-icon">
                        <FileOutlined />
                      </div>
                      <div className="dialog-popup-source-documents-source-details">
                        <div className="dialog-popup-source-documents-source-name">{error}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : documents.length === 0 ? (
                <div className="dialog-popup-source-documents-source-card">
                  <div className="dialog-popup-source-documents-source-header">
                    <div className="dialog-popup-source-documents-source-info">
                      <div className="dialog-popup-source-documents-source-icon">
                        <FileOutlined />
                      </div>
                      <div className="dialog-popup-source-documents-source-details">
                        <div className="dialog-popup-source-documents-source-name">
                          No associated files in this project.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                documents.map((document) => {
                  const hasFileTypeIcon = hasMappedFileTypeIcon(document.filename);

                  return (
                    <div key={document.id} className="dialog-popup-source-documents-source-card">
                      <div className="dialog-popup-source-documents-source-header">
                        <div className="dialog-popup-source-documents-source-info">
                          <div
                            className={`dialog-popup-source-documents-source-icon ${
                              hasFileTypeIcon ? "dialog-popup-source-documents-source-icon-file-type" : ""
                            }`}
                          >
                            <FileTypeIcon
                              filename={document.filename}
                              imageClassName="dialog-popup-source-documents-source-icon-image"
                            />
                          </div>
                          <div className="dialog-popup-source-documents-source-details">
                            <div className="dialog-popup-source-documents-source-name">{document.filename}</div>
                          </div>
                        </div>

                        <div className="dialog-popup-source-documents-source-actions">
                          <button
                            type="button"
                            onClick={() => {
                              downloadDocument(document.id);
                            }}
                            className="dialog-popup-source-documents-source-action"
                            title="Download Document"
                          >
                            <Tooltip title="Download Document">
                              <DownloadOutlined />
                            </Tooltip>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="dialog-popup-footer">
          <Button onClick={onClose} className="dialog-popup-footer-cancel">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export default SectionTagDocumentsModal;
